"use client";

import { type FC, useMemo } from "react";
import {
  ActionCard,
  Flex,
  Icon,
  Indicator,
  Spacing,
  type UIAction,
  useActions,
  useThemeSlot,
} from "@huub-web/ui";
import type {
  ThemeSlotPropsMap,
  ThemeScope,
} from "@huub-web/ui/contexts/ThemeSlotContext";

import { useTemplates } from "../../hooks/useTemplates";
import { usePageContent } from "../../hooks/usePageContent";

export type StepHubCardTemplateProps = {
  /**
   * The sequential step number displayed as a visual indicator
   */
  stepNumber: number;
  /**
   * Identifies the step for icon/visual resolution in theme slot overrides
   */
  scope?: ThemeScope;
  /**
   * The card title — always provided by the backend
   */
  title: string;
  /**
   * The card description — always provided by the backend
   */
  description: string;
  /**
   * Whether the step has been completed
   */
  completed?: boolean;
  /**
   * The action to perform when the card is clicked
   */
  action?: UIAction;
  /**
   * Whether the card is disabled
   */
  disabled?: boolean;
};

type DefaultStepCardProps = ThemeSlotPropsMap["hubStepCard"];

const DefaultStepCard: FC<DefaultStepCardProps> = ({
  stepNumber,
  title,
  description,
  action,
  completed,
  disabled,
}) => {
  const { handleAction } = useActions();

  const handleClick = () => {
    if (action) {
      handleAction(action);
    }
  };

  return (
    <ActionCard
      border="default"
      disabled={disabled}
      onClick={action ? handleClick : undefined}
      title={title}
      body={description}
      variant="secondary"
      padding="l"
      leading={
        <>
          <Flex justify="space-between">
            <Indicator size="l">{stepNumber}</Indicator>
            {completed && <Icon icon="checkCircleFilled" color="gradient" />}
          </Flex>
          <Spacing height="xxs" />
        </>
      }
    />
  );
};

export const StepHubCardTemplate: FC<StepHubCardTemplateProps> = ({
  stepNumber,
  scope,
  title,
  description,
  completed,
  action,
  disabled,
}) => {
  const { lockPrompts, lockUI } = useTemplates();

  const SlotComponent = useThemeSlot({
    key: "hubStepCard",
    defaultComponent: DefaultStepCard,
  });

  const contentItem = useMemo(
    () => ({
      type: "card" as const,
      label: title,
      description,
      meta: {
        stepNumber,
        ...(completed ? { completed } : {}),
        ...(scope ? { scope } : {}),
      },
      ...(action ? { action } : {}),
    }),
    [title, description, action, stepNumber, completed, scope],
  );
  usePageContent(`stephubcard-${stepNumber}`, contentItem);

  const isDisabled = (() => {
    if (action?.type === "prompt") {
      return lockPrompts;
    }
    return lockUI || disabled;
  })();

  return (
    <SlotComponent
      stepNumber={stepNumber}
      scope={scope}
      title={title}
      description={description}
      completed={completed}
      action={action}
      disabled={isDisabled}
    />
  );
};
